import { DateRange, DateRangeErrors, DateRangeResponse } from "../common/dateRange";
import { DateHelper } from "app/core/helpers/date.helper";

export interface EmployerResponse {
  id?: number;
  gross_working_wages: string;
  income_tax: string;
  solidarity_surcharge: string;
  pension_insurance_employer: string;
  pension_insurance_employee: string;
  health_insurance_employer_subsidies: string;
  private_health_insurance_employer_subsidies: string;
  care_insurance_employer_subsidies: string;
  health_insurance_employee_contributions: string;
  longterm_care_insurance: string;
  unemployment_insurance: string;
  proven_contributions_to_private_health_insurance: string;
  church_tax: string;
  retained_church_tax: string;
  business_trips_costs: string;
  trips_to_work_costs: string;
  wages_not_covered_by_reduced_tax: string;
  taxable_grants_for_external_activities: string;
  tax_free_employer_benefits: string;
  shortterm_allowance: string;
  tax_class?: number;
  period_of_employment?: DateRangeResponse;
  pension_benefits: string;
  reduced_tax_pension_benefits: string;
  reduced_tax_wages: string;
  payroll_tax_withheld: string;
  solidarity_surcharge_withheld: string;
  retained_church_tax_withheld: string;
  spouse_retained_church_tax_withheld: string;
  assessment_basis_for_pension: string;
  pension_benefits_commencement_year: string;
  pension_benefits_payments: DateRangeResponse;
  funeral_benefits: string;
}

export interface EmployerErrors {
  id?: string[];
  gross_working_wages?: string[];
  income_tax?: string[];
  solidarity_surcharge?: string[];
  pension_insurance_employer?: string[];
  pension_insurance_employee?: string[];
  health_insurance_employer_subsidies?: string[];
  private_health_insurance_employer_subsidies?: string[];
  care_insurance_employer_subsidies?: string[];
  health_insurance_employee_contributions?: string[];
  longterm_care_insurance?: string[];
  unemployment_insurance?: string[];
  proven_contributions_to_private_health_insurance?: string[];
  church_tax?: string[];
  retained_church_tax?: string[];
  business_trips_costs?: string[];
  trips_to_work_costs?: string[];
  wages_not_covered_by_reduced_tax?: string[];
  taxable_grants_for_external_activities?: string[];
  tax_free_employer_benefits?: string[];
  shortterm_allowance?: string[];
  tax_class?: string[];
  period_of_employment?: DateRangeErrors[];
  pension_benefits: string[];
  reduced_tax_pension_benefits: string[];
  reduced_tax_wages: string[];
  payroll_tax_withheld: string[];
  solidarity_surcharge_withheld: string[];
  retained_church_tax_withheld: string[];
  spouse_retained_church_tax_withheld: string[];
  assessment_basis_for_pension: string[];
  pension_benefits_commencement_year: string[];
  pension_benefits_payments: DateRangeErrors[];
  funeral_benefits: string[];
}

export class Employer {
  id: number = null;
  displayAmount = "";
  usedOCR = false;

  public useAdditionalCartFrom8To14 = false;
  public useAdditionalCartFrom29To32 = false;
  set ib03(ib: string) {
    this._ib03 = ib;
    let parsed = typeof ib === "string" ? Number(ib.replace(",", ".")) : 0;
    parsed = isNaN(parsed) || parsed > 1e20 || parsed < 0 ? 0 : parsed;
    this.displayAmount = parsed.toFixed(2) + " €";
  }

  get ib03(): string {
    return this._ib03;
  }

  ib04 = "";
  ib05 = "";
  ib06 = "";
  ib07 = "";
  ib15 = "";
  ib17 = "";
  ib18 = "";
  ib19 = "";
  ib20 = "";
  ib21 = "";
  ib22a = "";
  ib23a = "";
  ib24a = "";
  ib24b = "";
  ib24c = "";
  ib25 = "";
  ib26 = "";
  ib27 = "";
  ib28 = "";

  taxClass?: number;
  periodOfEmployment: DateRange = new DateRange();
  workDateStart = "";
  workDateEnd = "";
  pension_benefits = null;
  reduced_tax_pension_benefits = null;
  reduced_tax_wages = null;
  payroll_tax_withheld = null;
  solidarity_surcharge_withheld = null;
  retained_church_tax_withheld = null;
  spouse_retained_church_tax_withheld = null;
  assessment_basis_for_pension = null;
  pension_benefits_commencement_year = null;
  pension_benefits_payments: DateRange = new DateRange();
  funeral_benefits = null;

  pensionBenefitsPaymentsStart = null;
  pensionBenefitsPaymentsEnd = null;

  protected _ib03 = "";

  public isNonNullish(value) {
    if (value !== null || value !== undefined) return true;
    return false;
  }

  public isUseAdditionalCartFrom8To14Active(employer): boolean {
    const values = [
      employer.pension_benefits,
      employer.reduced_tax_pension_benefits,
      employer.reduced_tax_wages,
      employer.payroll_tax_withheld,
      employer.solidarity_surcharge_withheld,
      employer.retained_church_tax_withheld,
      employer.spouse_retained_church_tax_withheld
    ];

    return values.some(this.isNonNullish);
  }

  public isUseAdditionalCartFrom29To32Active(employer): boolean {
    const values = [
      employer.assessment_basis_for_pension,
      employer.pension_benefits_commencement_year,
      // employer.pension_benefits_payments,
      employer.funeral_benefits
    ];

    return values.some(this.isNonNullish);
  }

  static fromResponse(response: EmployerResponse): Employer {
    const employer = new Employer();
    employer.id = response.id;
    employer.ib03 = response.gross_working_wages;
    employer.ib04 = response.income_tax;
    employer.ib05 = response.solidarity_surcharge;
    employer.ib06 = response.church_tax;
    employer.ib07 = response.retained_church_tax;
    employer.ib15 = response.shortterm_allowance;
    employer.ib17 = response.business_trips_costs;
    employer.ib18 = response.trips_to_work_costs;
    employer.ib19 = response.wages_not_covered_by_reduced_tax;
    employer.ib20 = response.taxable_grants_for_external_activities;
    employer.ib21 = response.tax_free_employer_benefits;
    employer.ib22a = response.pension_insurance_employer;
    employer.ib23a = response.pension_insurance_employee;
    employer.ib24a = response.health_insurance_employer_subsidies;
    employer.ib24b = response.private_health_insurance_employer_subsidies;
    employer.ib24c = response.care_insurance_employer_subsidies;
    employer.ib25 = response.health_insurance_employee_contributions;
    employer.ib26 = response.longterm_care_insurance;
    employer.ib27 = response.unemployment_insurance;
    employer.ib28 = response.proven_contributions_to_private_health_insurance;
    employer.taxClass = response.tax_class;
    employer.periodOfEmployment = DateRange.fromResponse(response.period_of_employment);

    employer.pension_benefits = response.pension_benefits;
    employer.reduced_tax_pension_benefits = response.reduced_tax_pension_benefits;
    employer.reduced_tax_wages = response.reduced_tax_wages;
    employer.payroll_tax_withheld = response.payroll_tax_withheld;
    employer.solidarity_surcharge_withheld = response.solidarity_surcharge_withheld;
    employer.retained_church_tax_withheld = response.retained_church_tax_withheld;
    employer.spouse_retained_church_tax_withheld = response.spouse_retained_church_tax_withheld;
    employer.assessment_basis_for_pension = response.assessment_basis_for_pension;

    employer.pension_benefits_commencement_year = response.pension_benefits_commencement_year;
    employer.pension_benefits_payments = DateRange.fromResponse(response.pension_benefits_payments);
    employer.funeral_benefits = response.funeral_benefits;

    employer.useAdditionalCartFrom29To32 = employer.isUseAdditionalCartFrom29To32Active(employer);
    employer.useAdditionalCartFrom8To14 = employer.isUseAdditionalCartFrom8To14Active(employer);

    if (response.period_of_employment) {
      if (response.period_of_employment.lower) {
        employer.workDateStart = DateHelper.getFormatted(employer.periodOfEmployment.lower, "DD/MM");
      }
      if (response.period_of_employment.upper) {
        employer.workDateEnd = DateHelper.getFormatted(employer.periodOfEmployment.upper, "DD/MM");
      }
    } else {
      employer.workDateStart = null;
      employer.workDateEnd = null;
    }

    if (response.pension_benefits_payments) {
      if (response.pension_benefits_payments.lower) {
        employer.pensionBenefitsPaymentsStart = DateHelper.getFormatted(employer.pension_benefits_payments.lower, "MM");
      }
      if (response.pension_benefits_payments.upper) {
        employer.pensionBenefitsPaymentsEnd = DateHelper.getFormatted(employer.pension_benefits_payments.upper, "MM");
      }
    } else {
      employer.pensionBenefitsPaymentsStart = null;
      employer.pensionBenefitsPaymentsEnd = null;
    }

    return employer;
  }

  static fromStorage(response: Employer): Employer {
    const employer = new Employer();
    employer.id = response.id;
    employer.ib03 = response._ib03 || "0.00";
    employer.ib04 = response.ib04 || "0.00";
    employer.ib05 = response.ib05 || "0.00";
    employer.ib06 = response.ib06 || "0.00";
    employer.ib07 = response.ib07 || "0.00";
    employer.ib15 = response.ib15 || "0.00";
    employer.ib17 = response.ib17 || "0.00";
    employer.ib18 = response.ib18 || "0.00";
    employer.ib19 = response.ib19 || "0.00";
    employer.ib20 = response.ib20 || "0.00";
    employer.ib21 = response.ib21 || "0.00";
    employer.ib22a = response.ib22a || "0.00";
    employer.ib23a = response.ib23a || "0.00";
    employer.ib24a = response.ib24a || "0.00";
    employer.ib24b = response.ib24b || "0.00";
    employer.ib24c = response.ib24c || "0.00";
    employer.ib25 = response.ib25 || "0.00";
    employer.ib26 = response.ib26 || "0.00";
    employer.ib27 = response.ib27 || "0.00";
    employer.ib28 = response.ib28 || "0.00";

    employer.taxClass = response.taxClass;
    employer.periodOfEmployment = DateRange.fromResponse(response.periodOfEmployment);
    employer.workDateStart = response.workDateStart;
    employer.workDateEnd = response.workDateEnd;
    employer.pension_benefits_payments = DateRange.fromResponse(response.pension_benefits_payments);
    employer.pensionBenefitsPaymentsStart = response.pensionBenefitsPaymentsStart;
    employer.pensionBenefitsPaymentsEnd = response.pensionBenefitsPaymentsEnd;
    employer.funeral_benefits = response.funeral_benefits;
    return employer;
  }

  toResponse(defaultValue = undefined): EmployerResponse {
    const employer = {
      gross_working_wages: this.ib03 ? parseFloat(this.ib03).toFixed(2) : "0.00",
      income_tax: this.ib04 ? parseFloat(this.ib04).toFixed(2) : "0.00",
      solidarity_surcharge: this.ib05 ? parseFloat(this.ib05).toFixed(2) : "0.00",
      pension_insurance_employer: this.ib22a ? parseFloat(this.ib22a).toFixed(2) : "0.00",
      pension_insurance_employee: this.ib23a ? parseFloat(this.ib23a).toFixed(2) : "0.00",
      health_insurance_employer_subsidies: this.ib24a ? parseFloat(this.ib24a).toFixed(2) : "0.00",
      private_health_insurance_employer_subsidies: this.ib24b ? parseFloat(this.ib24b).toFixed(2) : "0.00",
      care_insurance_employer_subsidies: this.ib24c ? parseFloat(this.ib24c).toFixed(2) : "0.00",
      health_insurance_employee_contributions: this.ib25 ? parseFloat(this.ib25).toFixed(2) : "0.00",
      longterm_care_insurance: this.ib26 ? parseFloat(this.ib26).toFixed(2) : "0.00",
      unemployment_insurance: this.ib27 ? parseFloat(this.ib27).toFixed(2) : "0.00",
      proven_contributions_to_private_health_insurance: this.ib28 ? parseFloat(this.ib28).toFixed(2) : "0.00",
      church_tax: this.ib06 ? parseFloat(this.ib06).toFixed(2) : "0.00",
      retained_church_tax: this.ib07 ? parseFloat(this.ib07).toFixed(2) : "0.00",
      business_trips_costs: this.ib17 ? parseFloat(this.ib17).toFixed(2) : "0.00",
      trips_to_work_costs: this.ib18 ? parseFloat(this.ib18).toFixed(2) : "0.00",
      wages_not_covered_by_reduced_tax: this.ib19 ? parseFloat(this.ib19).toFixed(2) : "0.00",
      taxable_grants_for_external_activities: this.ib20 ? parseFloat(this.ib20).toFixed(2) : "0.00",
      tax_free_employer_benefits: this.ib21 ? parseFloat(this.ib21).toFixed(2) : "0.00",
      shortterm_allowance: this.ib15 ? parseFloat(this.ib15).toFixed(2) : "0.00",
      tax_class: this.taxClass ? this.taxClass : defaultValue,
      period_of_employment: this.periodOfEmployment.toResponse(),

      pension_benefits: this.pension_benefits ? parseInt(this.pension_benefits).toFixed(0) : null,
      reduced_tax_pension_benefits: this.reduced_tax_pension_benefits
        ? parseFloat(this.reduced_tax_pension_benefits).toFixed(0)
        : null,
      reduced_tax_wages: this.reduced_tax_wages ? parseFloat(this.reduced_tax_wages).toFixed(0) : null,
      payroll_tax_withheld: this.payroll_tax_withheld ? parseFloat(this.payroll_tax_withheld).toFixed(0) : null,
      solidarity_surcharge_withheld: this.solidarity_surcharge_withheld
        ? parseFloat(this.solidarity_surcharge_withheld).toFixed(2)
        : null,
      retained_church_tax_withheld: this.retained_church_tax_withheld
        ? parseFloat(this.retained_church_tax_withheld).toFixed(2)
        : null,
      spouse_retained_church_tax_withheld: this.spouse_retained_church_tax_withheld
        ? parseFloat(this.spouse_retained_church_tax_withheld).toFixed(2)
        : null,
      assessment_basis_for_pension: this.assessment_basis_for_pension
        ? parseFloat(this.assessment_basis_for_pension).toFixed(0)
        : null,
      pension_benefits_commencement_year: this.pension_benefits_commencement_year ?? null,
      pension_benefits_payments: this.pension_benefits_payments ? this.pension_benefits_payments.toResponse() : null,
      funeral_benefits: this.funeral_benefits ? parseFloat(this.funeral_benefits).toFixed(0) : null
    };

    if (this.id) {
      employer["id"] = this.id;
    }

    return employer;
  }

  isEmpty(): boolean {
    const keys = Object.keys(this);
    const employerInputFields = [
      "ib03",
      "ib04",
      "ib05",
      "ib06",
      "ib07",
      "ib15",
      "ib17",
      "ib18",
      "ib19",
      "ib20",
      "ib21",
      "ib22a",
      "ib23a",
      "ib24a",
      "ib24b",
      "ib24c",
      "ib25",
      "ib26",
      "ib27",
      "ib28"
    ];

    for (const key of keys) {
      if (["id", "periodOfEmployment", "taxClass", "displayAmount", "pension_benefits_payments"].indexOf(key) < 0) {
        for (const inputKey of employerInputFields) {
          if (this[inputKey] !== "0.00") {
            return false;
          }
        }
      }
    }

    return !(
      this.periodOfEmployment.lower !== null ||
      this.periodOfEmployment.upper !== null ||
      this.pension_benefits_payments.lower !== null ||
      this.pension_benefits_payments.upper !== null ||
      this.taxClass !== null
    );
  }

  clear(): void {
    this.periodOfEmployment = new DateRange();
    this.periodOfEmployment.lower = null;
    this.periodOfEmployment.upper = null;

    this.pension_benefits_payments = new DateRange();
    this.pension_benefits_payments.lower = null;
    this.pension_benefits_payments.upper = null;

    this.taxClass = null;
    this.formatEmployerFields(this);
  }

  public formatPeriodOfEmployment(fullYear: string | number): void {
    let dateStart, dateEnd;
    if (this.workDateStart) {
      this.workDateStart = this.workDateStart.replace(".", "/").replace("-", "/");

      dateStart = DateHelper.getInstance(this.workDateStart + "/" + fullYear, "DD/MM/YYYY", true);
    }
    if (dateStart && dateStart.isValid()) {
      this.workDateStart = dateStart.format("DD/MM");
      this.periodOfEmployment.lower = dateStart.format("YYYY-MM-DD");
    } else {
      this.periodOfEmployment.lower = null;
    }

    if (this.workDateEnd) {
      this.workDateEnd = this.workDateEnd.replace(".", "/").replace("-", "/");

      dateEnd = DateHelper.getInstance(this.workDateEnd + "/" + fullYear, "DD/MM/YYYY", true);
    }
    if (dateEnd && dateEnd.isValid()) {
      this.workDateEnd = dateEnd.format("DD/MM");
      this.periodOfEmployment.upper = dateEnd.format("YYYY-MM-DD");
    } else {
      this.periodOfEmployment.upper = null;
    }
  }

  public formatPensionBenefitsPayments(): void {
    if (this.pensionBenefitsPaymentsStart) {
      this.pension_benefits_payments.lower = this.pensionBenefitsPaymentsStart;
    } else {
      this.pension_benefits_payments.lower = null;
    }

    if (this.pensionBenefitsPaymentsEnd) {
      this.pension_benefits_payments.upper = this.pensionBenefitsPaymentsEnd;
    } else {
      this.pension_benefits_payments.upper = null;
    }
  }

  private formatEmployerFields(employer): void {
    const fieldsToCalc = Object.keys(this).filter((key) => key.includes("ib"));
    fieldsToCalc.forEach((field) => {
      employer[field] = "0.00";
    });
    this.ib03 = "0.00";
  }
}
